import React from "react";
import { useState, useEffect } from "react";
import { DatePickerInput } from "./date-picker-input";
import { Calendar } from "./calendar";

export type DatePickerProps = {
  /** sets the default selected date in date picker */
  selectedDate?: Date;
  /** sets the secondary selected date in date picker */
  selectedDateSec?: Date;
  /** sets the due date in date picker */
  dueDate?: Date;
  /** set whether a range */
  isRange?: boolean;
  /** array of dates that will be disabled in the date picker */
  disabledDates?: Date[];
  /** set the active start date in date picker, everything before will be disabled */
  activeStartDate?: Date;
  /** set the active end date in date picker, everything after will be disabled */
  activeEndDate?: Date;
  /** set the alert message in calendar */
  alertMessage?: string;

  /** event triggered when a date is selected from primary date picker */
  onSelect?: (val: Date) => void;
  /** event triggered when a date is selected from secondary date picker in case of range selection */
  onSecSelect?: (val: Date) => void;

  /** sets the label for date picker input and primary date picker input in case of range selection */
  labelText?: string;
  /** sets the label for secondary date picker input in case of range selection */
  labelTextSecondary?: string;
  /** sets the placeholder for date picker input */
  placeHolderText?: string;
  /** sets the due date legend label */
  dueDateLabel?: string;
  /** sets the selected date legend label */
  selectedDateLabel?: string;
  /** sets the selected due date legend label */
  selectedDueDateLabel?: string;
  /** disables the date picker when set to true */
  disabled?: boolean;
  /** hides the next month icon when reaching the date provided in this props */
  disableNext?: Date;
  /** hides the prev month icon when reaching the date provided in this props */
  disablePrev?: Date;
  /** whether legend at bottom is shown */
  showLegendAtBottom?: boolean;
  /** set true if want to show calender only */
  showCalenderOnly?: boolean;
  /** custom error text for daterange */
  errorMsg?: string;  
  /** sets the errorMsg for secondary date picker input in case of range selection */
  errorMsgSecondary?: string;
  /** This will be set as id for date picker input */
  testId?: string;
  /** used for unique identification of element */
  id: string;
  /** used for tooltip */
  tooltip?: any;
  /** sets the tooltip for secondary date picker input in case of range selection */
  tooltipSecondary?: any;
};

// for memo: export const DatePicker = React.memo(({
export const DatePicker = ({ ...props }: DatePickerProps) => {
  // HOOKS ##############################################################

  /* stateful items */
  const [startDate, setStartDate] = useState<Date | number>(
    props.selectedDate ? props.selectedDate : -1
  );
  const [endDate, setEndDate] = useState<Date | number>(
    props.selectedDateSec ? props.selectedDateSec : -1
  );
  const [selectedDate, setSelectedDate] = useState(props.selectedDate);
  const [secSelectedDate, setSecSelectedDate] = useState(props.selectedDateSec);

  /* asynchronous after render with cleanup - similar to componentDidMount, but not the same since layout is not available */
  useEffect(() => {
    if (endDate !== -1 && selectedDate > endDate) {
      setEndDate(-1);
      setSecSelectedDate(undefined);
    }
  }, []);

  useEffect(() => {    
      setSelectedDate(props.selectedDate);
  }, [props.selectedDate]);

  useEffect(() => {        
    setSecSelectedDate(props.selectedDateSec);
  }, [props.selectedDateSec]);

  // METHODS #############################################################

  const onSelect = (date: Date) => {
    setStartDate(date);
    setSelectedDate(date);

    if (endDate !== -1 && date > endDate) {
      setEndDate(-1);
      setSecSelectedDate(undefined);
    }

    if (props.onSelect) {
      props.onSelect(date);
    }
  };

  const onSecSelect = (date: Date) => {
    setEndDate(date);
    setSecSelectedDate(date);

    if (props.onSecSelect) {
      props.onSecSelect(date);
    }
  };

  // RENDER ##############################################################

  return (
    <>
      <div
        id={props.testId}
        className={`${props.showCalenderOnly ? `calender320` : ``}`}
        data-testid={props.testId}
      >
        <div className={" pad-b-none"}>
          {props.showCalenderOnly ? (
            <>
              <div className="row mar-b-xxs">
                <label id={`date-label-${props.id}`} className={`heading-sm`}>
                  {props.labelText}
                </label>
              </div>
              <Calendar
                testId={props.testId}
                id={props.id}
                selectedDate={selectedDate}
                dueDate={props.dueDate}
                alertMessage={props.alertMessage}
                disabledDates={props.disabledDates}
                activeStartDate={props.activeStartDate}
                activeEndDate={props.activeEndDate}
                disableNext={props.disableNext}
                disablePrev={props.disablePrev}
                showLegendAtBottom={props.showLegendAtBottom}
                labelText={props.labelText}
                dueDateLabel={props.dueDateLabel}
                selectedDateLabel={props.selectedDateLabel}
                selectedDueDateLabel={props.selectedDueDateLabel}
                onSelect={onSelect}
                showCalendar={true}
                showCalenderOnly={props.showCalenderOnly}
              />
            </>
          ) : props.isRange ? (
            <div className={"inline-form-row"}>
              <div className={`form-row nopad`}>
                <DatePickerInput
                  testId={`${props.testId}-prim`}
                  id={`${props.id}prim`}
                  dueDate={props.dueDate}
                  startDate={startDate}
                  endDate={endDate}
                  isStartDate={true}
                  isRange={true}
                  selectedDate={selectedDate}
                  disabledDates={props.disabledDates ? props.disabledDates : []}
                  activeStartDate={props.activeStartDate}
                  activeEndDate={props.activeEndDate}
                  showCalenderOnly={props.showCalenderOnly}
                  alertMessage={props.alertMessage}
                  onSelect={onSelect}
                  labelText={props.labelText ? props.labelText : ``}
                  placeHolderText={props.placeHolderText}
                  dueDateLabel={props.dueDateLabel}
                  selectedDateLabel={props.selectedDateLabel}
                  selectedDueDateLabel={props.selectedDueDateLabel}
                  disabled={props.disabled}
                  disableNext={props.disableNext}
                  disablePrev={props.disablePrev}
                  showLegendAtBottom={props.showLegendAtBottom}
                  errorMsg={props.errorMsg}
                  tooltip={props.tooltip}
                />
              </div>
              <div className={`form-row nopad`}>
                <DatePickerInput
                  testId={`${props.testId}-sec`}
                  id={`${props.id}sec`}
                  dueDate={props.dueDate}
                  startDate={startDate}
                  endDate={endDate}
                  isEndDate={true}
                  isRange={true}
                  selectedDate={secSelectedDate}
                  disabledDates={props.disabledDates ? props.disabledDates : []}
                  activeStartDate={props.activeStartDate}
                  activeEndDate={props.activeEndDate}
                  showCalenderOnly={props.showCalenderOnly}
                  alertMessage={props.alertMessage}
                  onSelect={onSecSelect}
                  labelText={
                    props.labelTextSecondary
                      ? props.labelTextSecondary
                      : props.labelText
                      ? props.labelText
                      : ``
                  }
                  placeHolderText={props.placeHolderText}
                  dueDateLabel={props.dueDateLabel}
                  selectedDateLabel={props.selectedDateLabel}
                  selectedDueDateLabel={props.selectedDueDateLabel}
                  disabled={props.disabled}
                  disableNext={props.disableNext}
                  disablePrev={props.disablePrev}
                  showLegendAtBottom={props.showLegendAtBottom}
                  errorMsg={props.errorMsgSecondary}
                  tooltip={props.tooltipSecondary}
                />
              </div>
            </div>
          ) : (
            <DatePickerInput
              id={props.id}
              testId={props.testId}
              dueDate={props.dueDate}
              selectedDate={selectedDate}
              disabledDates={props.disabledDates ? props.disabledDates : []}
              activeStartDate={props.activeStartDate}
              activeEndDate={props.activeEndDate}
              showCalenderOnly={props.showCalenderOnly}
              alertMessage={props.alertMessage}
              onSelect={onSelect}
              labelText={props.labelText}
              placeHolderText={props.placeHolderText}
              dueDateLabel={props.dueDateLabel}
              selectedDateLabel={props.selectedDateLabel}
              selectedDueDateLabel={props.selectedDueDateLabel}
              disabled={props.disabled}
              disableNext={props.disableNext}
              disablePrev={props.disablePrev}
              showLegendAtBottom={props.showLegendAtBottom}
              errorMsg={props.errorMsg}
              tooltip={props.tooltip}
            />
          )}
        </div>
      </div>
      <style jsx>
        {`
          @media (max-width: 320px) {
            .calender320 {
              width: fit-content;
            }
          }
        `}
      </style>
    </>
  );
};
